import { DI } from '@intigral/constant/config/DIContainer';
import { container } from '../../DataDomain';
import {
    IAddBookMarkPayload,
    IBookMarkResponse,
    IBookMarkSearchParams,
    IBookMarkDetailResponse,
    IWatchAgainResponse,
    IWatchAgainSearchParams,
} from '../../entity/interface/iBookmark';
import { IAddBookmarkUseCase } from '../../core/usecases/bookmark/AddBookmarkUseCase';
import { IGetBookmarkUseCase } from '../../core/usecases/bookmark/GetBookmarkUseCase';
import { IGetDetailBookmarkUseCase } from '../../core/usecases/bookmark/GetDetailBookmarkUseCase';
import { IDeleteBookmarkUseCase } from '../../core/usecases/bookmark/DeleteBookmarkUseCase';
import { IGetWatchAgainUseCase } from '../../core/usecases/bookmark/GetWatchAgainUseCase';
import { GUEST_PROFILE_GUID } from '@intigral/constant';

export interface IBookmarkService {
    addBookmark(
        apiKey: string,
        profileGuid: string,
        payload: IAddBookMarkPayload,
        token: string,
        identityToken: string,
        isGamificationEnabled?: boolean
    ): Promise<boolean>;

    getBookmark(apiKey: string, profileGuid: string, token: string, identityToken: string): Promise<IBookMarkResponse>;

    getDetailBookmark(
        apiKey: string,
        profileGuid: string,
        token: string,
        identityToken: string,
        searchParams: IBookMarkSearchParams,
        datasource?: string
    ): Promise<IBookMarkDetailResponse>;

    deleteBookmark(
        apiKey: string,
        profileGuid: string,
        token: string,
        identityToken: string,
        assetId: string,
        paId: string,
        src?: string
    ): Promise<boolean>;

    getWatchAgain(
        apiKey: string,
        profileName: string,
        token: string,
        identityToken: string,
        searchParams: IWatchAgainSearchParams
    ): Promise<IWatchAgainResponse>;
}

export class BookmarkService implements IBookmarkService {
    // Lazy getters for dependencies to avoid circular dependencies
    private get addBookmarkUseCase(): IAddBookmarkUseCase {
        return container.resolve<IAddBookmarkUseCase>(DI.USECASES.IAddBookmarkUseCase);
    }

    private get getBookmarkUseCase(): IGetBookmarkUseCase {
        return container.resolve<IGetBookmarkUseCase>(DI.USECASES.IGetBookmarkUseCase);
    }

    private get getDetailBookmarkUseCase(): IGetDetailBookmarkUseCase {
        return container.resolve<IGetDetailBookmarkUseCase>(DI.USECASES.IGetBookmarkDetailUseCase);
    }

    private get deleteBookmarkUseCase(): IDeleteBookmarkUseCase {
        return container.resolve<IDeleteBookmarkUseCase>(DI.USECASES.IDeleteBookmarkUseCase);
    }

    private get getWatchAgainUseCase(): IGetWatchAgainUseCase {
        return container.resolve<IGetWatchAgainUseCase>(DI.USECASES.IGetWatchAgainUseCase);
    }

    constructor() {
        // No initialization needed - dependencies resolved lazily via getters
    }

    async addBookmark(
        apiKey: string,
        profileGuid: string,
        paylod: IAddBookMarkPayload,
        token: string,
        identityToken: string
    ): Promise<boolean> {
        if (!profileGuid || profileGuid === GUEST_PROFILE_GUID) {
            return false;
        }
        return await this.addBookmarkUseCase.execute(apiKey, profileGuid, paylod, token, identityToken);
    }

    async getBookmark(apiKey: string, profileGuid: string, token: string, identityToken: string): Promise<any> {
        if (!profileGuid || profileGuid === GUEST_PROFILE_GUID) {
            return { data: [] };
        }
        return await this.getBookmarkUseCase.execute(apiKey, profileGuid, token, identityToken);
    }

    async getDetailBookmark(
        apiKey: string,
        profileGuid: string,
        token: string,
        identityToken: string,
        searchParams: IBookMarkSearchParams,
        datasource?: string
    ): Promise<IBookMarkDetailResponse> {
        if (!profileGuid || profileGuid === GUEST_PROFILE_GUID) {
            return {
                txRef: '',
                data: {
                    totalCount: 0,
                    pageSize: 0,
                    pageNumber: 0,
                    count: 0,
                    entries: [],
                },
            };
        }
        return await this.getDetailBookmarkUseCase.execute(
            apiKey,
            profileGuid,
            token,
            identityToken,
            searchParams,
            datasource
        );
    }

    async deleteBookmark(
        apiKey: string,
        profileGuid: string,
        token: string,
        identityToken: string,
        assetId: string,
        paId: string,
        src?: string
    ): Promise<any> {
        if (!profileGuid || profileGuid === GUEST_PROFILE_GUID) {
            return false;
        }
        return await this.deleteBookmarkUseCase.execute(
            apiKey,
            profileGuid,
            token,
            identityToken,
            assetId,
            paId,
            src
        );
    }

    async getWatchAgain(
        apiKey: string,
        profileName: string,
        token: string,
        identityToken: string,
        searchParams: IWatchAgainSearchParams
    ): Promise<any> {
        return await this.getWatchAgainUseCase.execute(apiKey, profileName, token, identityToken, searchParams);
    }
}
